import React from 'react';
import { 
  MessageSquare, 
  ArrowLeft, 
  Send, 
  X, 
  Plus, 
  AlertCircle, 
  MoreVertical, 
  Eye,
  Users 
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';

// Helper to format message timestamp
const formatMessageTimestamp = (timestamp, options = {}) => {
  if (!timestamp) return 'N/A';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'N/A';
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  };
  return date.toLocaleString('en-US', { ...defaultOptions, ...options });
};

/**
 * MessagesDropdown - Dropdown component for staff messaging
 */
export const MessagesDropdown = ({
  // User info
  user,
  users = [],
  
  // Core state
  showStaffMessages,
  setShowStaffMessages,
  staffMessageThreads,
  selectedStaffThread,
  staffThreadMessages,
  staffReplyText,
  setStaffReplyText,
  sendingStaffReply,
  loadingStaffMessages,
  unreadStaffMessagesCount,
  
  // Menu state
  openMessageMenu,
  setOpenMessageMenu,
  
  // Announcements
  staffAnnouncements = [],
  unreadAnnouncementsCount = 0,
  onMarkAnnouncementRead,
  
  // Functions
  sendStaffReply,
  fetchStaffThreadMessages,
  openThread,
  closeThread,
  
  // Dialog triggers
  onNewMessage,
  onForward,
  onRecall,
  setThreadToForward,
  setShowForwardDialog,
  setRecallThreadId,
  setShowRecallDialog,
}) => {
  const [activeTab, setActiveTab] = React.useState('messages'); // 'messages' or 'announcements'
  const wrapperRef = React.useRef(null);

  // Close dropdown on outside click or Escape (Feb 2026, parity with NotificationBell).
  // Skipped when a child Dialog is open (forward/recall) so clicking inside
  // those modals doesn't collapse the dropdown behind them.
  React.useEffect(() => {
    if (!showStaffMessages) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && wrapperRef.current.contains(event.target)) return;
      // Don't close if the click landed inside an open Radix dialog/portal —
      // forward/recall dialogs render outside this wrapper via React portals.
      const target = event.target;
      if (target && target.closest && target.closest('[role="dialog"], [data-radix-popper-content-wrapper]')) {
        return;
      }
      setShowStaffMessages(false);
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        // If a thread is open, close the thread first; otherwise close the dropdown.
        if (selectedStaffThread) {
          closeThread();
        } else {
          setShowStaffMessages(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showStaffMessages, setShowStaffMessages, selectedStaffThread, closeThread]);

  if (!user) return null;

  const totalUnread = unreadStaffMessagesCount + unreadAnnouncementsCount;

  return (
    <div className="relative" ref={wrapperRef} data-testid="messages-dropdown-wrapper">
      {/* Mar 2026 — styled to match the V2 NotificationBell trigger
          (square outline border was visually heavy next to the
          borderless bell button — user-reported "why is there a
          square around the chat icon in V2?").  Same dimensions,
          hover-only background, rounded corners. */}
      <button
        type="button"
        onClick={() => setShowStaffMessages(!showStaffMessages)}
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
        data-testid="messages-button"
        aria-label="Staff messages"
        title="Staff messages"
      >
        <MessageSquare className="h-4 w-4" />
        {totalUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-teal-500 text-[10px] text-white font-semibold flex items-center justify-center px-1">
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>
      
      {/* Messages Dropdown */}
      {showStaffMessages && (
        <div className="absolute right-0 mt-2 w-[600px] bg-white rounded-lg shadow-lg border z-50" style={{maxHeight: '600px'}}>
          {selectedStaffThread ? (
            /* Thread View */
            <div className="flex flex-col h-full">
              <div className="p-3 border-b flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeThread}
                  data-testid="messages-back-button"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Messages
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowForwardDialog(true)}
                    className="text-teal-600 border-teal-600 hover:bg-teal-50"
                    data-testid="add-to-thread-button"
                  >
                    <Send className="h-3 w-3 mr-2" />
                    Add Staff
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRecallThreadId(selectedStaffThread.id);
                      setShowRecallDialog(true);
                    }}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    data-testid="recall-message-button"
                  >
                    <X className="h-3 w-3 mr-2" />
                    Recall
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowStaffMessages(false)}
                    data-testid="close-messages-button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="p-3 border-b bg-gray-50">
                <h3 className="font-semibold text-lg">{selectedStaffThread.subject}</h3>
                <div className="flex items-center gap-3 text-sm text-gray-600 mt-1 flex-wrap">
                  {selectedStaffThread.sender_id === user?.id ? (
                    <span>To: {selectedStaffThread.recipient_name || 'Staff Member'}</span>
                  ) : (
                    <span>From: {selectedStaffThread.client_name || selectedStaffThread.sender_name || 'Staff Member'}</span>
                  )}
                  <span>•</span>
                  <span>Topic: {selectedStaffThread.topic?.replace('_', ' ')}</span>
                  {/* Delegation indicator */}
                  {user?.is_scheduler && 
                   user?.scheduling_for?.includes(selectedStaffThread.recipient_id) && 
                   selectedStaffThread.recipient_id !== user.id && (() => {
                    const delegatedProvider = users.find(u => u.id === selectedStaffThread.recipient_id);
                    return delegatedProvider ? (
                      <>
                        <span>•</span>
                        <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          On behalf of {delegatedProvider.full_name || `${delegatedProvider.first_name} ${delegatedProvider.last_name}`}
                        </span>
                      </>
                    ) : null;
                  })()}
                </div>
                {/* Show thread participants */}
                {(() => {
                  const sharedWith = selectedStaffThread.shared_with || [];
                  if (sharedWith.length === 0) return null;
                  
                  const sharedUsers = sharedWith
                    .map(id => users.find(u => u.id === id))
                    .filter(Boolean)
                    .map(u => u.full_name || `${u.first_name} ${u.last_name}`.trim());
                  
                  if (sharedUsers.length === 0) return null;
                  
                  return (
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <Users className="h-3 w-3 text-gray-500" />
                      <span className="text-gray-500">Also on thread:</span>
                      <span className="text-gray-700">{sharedUsers.join(', ')}</span>
                    </div>
                  );
                })()}
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{maxHeight: '300px'}}>
                {staffThreadMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] rounded-lg p-3 ${
                        msg.sender_id === user.id
                          ? 'bg-teal-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <div className="font-semibold text-xs mb-1">{msg.sender_name}</div>
                      <div className="text-sm whitespace-pre-wrap">{msg.message}</div>
                      <div className="text-xs mt-1 opacity-75">
                        {formatMessageTimestamp(msg.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-3 border-t bg-gray-50">
                <textarea
                  placeholder="Type your reply..."
                  value={staffReplyText}
                  onChange={(e) => setStaffReplyText(e.target.value)}
                  className="w-full p-2 border-2 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                  rows="3"
                  data-testid="message-reply-input"
                />
                <div className="flex justify-end mt-2">
                  <Button
                    onClick={sendStaffReply}
                    disabled={sendingStaffReply || !staffReplyText.trim()}
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="send-reply-button"
                  >
                    {sendingStaffReply ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-3 w-3 mr-2" />
                        Send Reply
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* Messages/Announcements List */
            <>
              {/* Tabs */}
              <div className="flex border-b">
                <button
                  className={`flex-1 px-4 py-2 text-sm font-medium ${activeTab === 'messages' ? 'border-b-2 border-teal-600 text-teal-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveTab('messages')}
                >
                  Messages
                  {unreadStaffMessagesCount > 0 && (
                    <span className="ml-2 bg-teal-100 text-teal-700 text-xs px-2 py-0.5 rounded-full">
                      {unreadStaffMessagesCount}
                    </span>
                  )}
                </button>
                <button
                  className={`flex-1 px-4 py-2 text-sm font-medium ${activeTab === 'announcements' ? 'border-b-2 border-teal-600 text-teal-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveTab('announcements')}
                >
                  Announcements
                  {unreadAnnouncementsCount > 0 && (
                    <span className="ml-2 bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                      {unreadAnnouncementsCount}
                    </span>
                  )}
                </button>
              </div>

              {activeTab === 'messages' ? (
                /* Messages Tab */
                <>
                  <div className="p-3 border-b flex justify-between items-center">
                    <h3 className="font-semibold">Messages</h3>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (onNewMessage) onNewMessage();
                          setTimeout(() => setShowStaffMessages(false), 100);
                        }}
                        className="text-teal-600 border-teal-600 hover:bg-teal-50"
                        data-testid="new-message-button"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        New
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowStaffMessages(false)}
                        data-testid="close-messages-list-button"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
              
                  {/* Archive Notice */}
              <div className="p-3 bg-blue-50 border-b border-blue-200">
                <p className="text-xs text-blue-800">
                  <AlertCircle className="h-3 w-3 inline mr-1" />
                  Messages are archived after 180 days of inactivity
                </p>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {loadingStaffMessages ? (
                  <div className="p-8 text-center text-gray-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-2"></div>
                    <p>Loading messages...</p>
                  </div>
                ) : staffMessageThreads.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No messages yet</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {staffMessageThreads.map((thread) => {
                      // Check if this is a delegated message
                      const isDelegated = user?.is_scheduler && 
                                         user?.scheduling_for?.includes(thread.recipient_id) && 
                                         thread.recipient_id !== user.id;
                      const delegatedProvider = isDelegated ? users.find(u => u.id === thread.recipient_id) : null;
                      
                      // Determine display label
                      const isUserSender = thread.sender_id === user?.id;
                      const displayLabel = isUserSender ? 'To' : 'From';
                      const displayName = isUserSender 
                        ? (thread.recipient_name || 'Staff Member')
                        : (thread.client_name || thread.sender_name || 'Staff Member');
                      
                      return (
                        <div
                          key={thread.id}
                          className={`p-3 hover:bg-teal-50 transition-colors cursor-pointer ${isDelegated ? 'border-l-4 border-purple-400 bg-purple-50/30' : ''}`}
                          onClick={() => openThread(thread)}
                          data-testid={`message-thread-${thread.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {thread.is_urgent && <span className="text-red-600">🔴</span>}
                                {isDelegated && delegatedProvider && (
                                  <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    On behalf of {delegatedProvider.full_name || `${delegatedProvider.first_name} ${delegatedProvider.last_name}`}
                                  </span>
                                )}
                                <h4 className="font-semibold text-sm">{thread.subject}</h4>
                                {thread.unread_count_staff > 0 && (
                                  <span className="bg-teal-600 text-white text-xs px-2 py-0.5 rounded-full">
                                    {thread.unread_count_staff} new
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600">{displayLabel}: {displayName}</p>
                              <p className="text-xs text-gray-600 truncate mt-1">{thread.last_message_preview}</p>
                            </div>
                            <div className="flex items-center gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                              <div className="text-xs text-gray-500">
                                {formatMessageTimestamp(thread.last_message_at, { hour: undefined, minute: undefined })}
                              </div>
                              <Popover 
                                open={openMessageMenu === thread.id}
                                onOpenChange={(open) => setOpenMessageMenu(open ? thread.id : null)}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`message-menu-${thread.id}`}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-48 p-1 z-[60]" align="end">
                                  <div className="flex flex-col gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="justify-start rounded-md hover:bg-gray-100 h-9"
                                      onClick={() => {
                                        openThread(thread);
                                        setOpenMessageMenu(null);
                                      }}
                                    >
                                      <Eye className="h-4 w-4 mr-2" />
                                      Open
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="justify-start rounded-md hover:bg-teal-50 text-teal-600 h-9"
                                      onClick={() => {
                                        setThreadToForward(thread);
                                        setShowForwardDialog(true);
                                        setOpenMessageMenu(null);
                                      }}
                                    >
                                      <Send className="h-4 w-4 mr-2" />
                                      Add Staff
                                    </Button>
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
                </>
              ) : (
                /* Announcements Tab */
                <>
                  <div className="p-3 border-b flex justify-between items-center">
                    <h3 className="font-semibold">Announcements</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowStaffMessages(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto">
                    {staffAnnouncements.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                        <p>No announcements</p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {staffAnnouncements.map((announcement) => (
                          <div
                            key={announcement.id}
                            className={`p-4 ${!announcement.is_read ? 'bg-amber-50' : ''}`}
                            onClick={() => {
                              if (!announcement.is_read && onMarkAnnouncementRead) {
                                onMarkAnnouncementRead(announcement.id);
                              }
                            }}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-sm flex items-center gap-2">
                                {!announcement.is_read && (
                                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                                )}
                                {announcement.subject}
                              </h4>
                              <span className="text-xs text-gray-500">
                                {formatMessageTimestamp(announcement.created_at, { hour: undefined, minute: undefined })}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{announcement.message}</p>
                            <p className="text-xs text-gray-500 mt-2">From: {announcement.created_by_name}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MessagesDropdown;
